/**
 * The phone's draft screen — built to turn `8b`.
 *
 * "Opens on your own column, as the app already does under 760px." The drawing's reasoning, and
 * it is right: on a phone you are checking what your own slate looks like after three rounds, not
 * reading ten columns. What the app did instead was open the TEN-card teams view, which is the
 * same information at ten times the scrolling — your card was first, but nine others were still
 * under it.
 *
 * So this is one card of your picks and one of your needs, and nothing else. The board and the
 * available list are each one tap away.
 *
 * Two details from the drawing that carry the weight:
 *
 *   YOUR NEXT PICK IS MARKED IN THE LIST, not stated separately — `4.08 — your next`. The list is
 *   already in draft order, so the marker turns it into a position in a queue for free.
 *
 *   A KEEPER SAYS `keeper`, A PICK SAYS `pick`. Same row shape either way. On a phone there is no
 *   room for a tint to carry that, and "why is Bijan on my 2.10" is the question a GM asks once a
 *   year and then asks again.
 */

import { managerShort } from '../domain/identity.js';
import { pickCode, type BoardPick } from '../domain/board.js';
import { STARTERS } from '../domain/draftTools.js';
import { shortSurname, splitName } from './BoardGrid.js';
import { nflInfoOf } from './NflChip.js';

/** The five slots the needs card reports, in the drawing's order. */
const SLOTS = ['QB', 'RB', 'WR', 'TE', 'DEF'] as const;

/**
 * How a slot reads: `full`, `empty`, or `1 of 2`. The drawing's own three forms, and each says
 * something different — `1 of 2` is the only one that needs a number, because it is the only one
 * where the number is the news.
 */
export function slotState(have: number, starters: number): string {
  if (have >= starters) return 'full';
  if (have === 0) return 'empty';
  return `${have} of ${starters}`;
}

export function MyDraft(
  { board, me, teams, clock, moved, onGoBoard }: {
    board: readonly BoardPick[]; me: string; teams: number;
    /** The pick on the clock, so "your next" can say how far away it is (17e). */
    clock?: BoardPick | null;
    /** Keepers whose charged round was moved by a commissioner — the MOVED pill. */
    moved?: ReadonlyMap<number, string>;
    onGoBoard: () => void;
  },
) {
  const mine = board
    .filter((p) => p.owner.toUpperCase() === me.toUpperCase())
    .sort((a, b) => a.overall - b.overall);
  const made = mine.filter((p) => p.player && !p.keeper).length;
  const kept = mine.filter((p) => p.keeper).length;
  const next = mine.find((p) => !p.player) ?? null;

  const have = (pos: string) => mine.filter((p) => p.player && (p.pos ?? '') === pos).length;
  /*
   * 17e: one line, the open slots in lineup order — `FLEX, DEF, RB +1`. A slot that is short by
   * one is named; short by more says by how many. FLEX counts every RB/WR/TE over the starters.
   */
  const rostered = mine.filter((p) => p.player);
  const flexPool = rostered.filter((p) => ['RB', 'WR', 'TE'].includes(p.pos ?? '')).length
    - STARTERS.filter((s) => ['RB', 'WR', 'TE'].includes(s.pos)).reduce((n, s) => n + s.n, 0);
  const fixedShort = STARTERS.some((s) => ['RB', 'WR', 'TE'].includes(s.pos) && have(s.pos) < s.n);
  const open = STARTERS.map(({ pos, n }) => {
    // FLEX is only a need once the fixed RB/WR/TE slots are filled; before that it is the same hole.
    const short = pos === 'FLEX' ? (fixedShort ? 0 : n - Math.max(0, flexPool)) : n - have(pos);
    return short > 0 ? { pos, short } : null;
  }).filter((x): x is { pos: string; short: number } => x !== null);
  const needsLine = open.length === 0 ? 'lineup full'
    : open.map((o) => (o.short > 1 ? `${o.pos} +${o.short}` : o.pos)).join(', ');
  const away = clock && next ? next.overall - clock.overall : null;

  return (
    <div className="mydraft">
      <section className="card mdpicks">
        <div className="striptop">
          <h3 className="striphead">Your picks, in draft order</h3>
          <span className="mut">
            {kept} keeper{kept === 1 ? '' : 's'}, {made} made
          </span>
        </div>
        <ul>
          {mine.map((p) => {
            const code = pickCode(p, teams);
            const isNext = next?.overall === p.overall;
            const from = p.owner.toUpperCase() !== p.seat.toUpperCase()
              ? ` from ${managerShort(p.seat)}` : '';
            if (!p.player) {
              return (
                <li className={`mdrow open ${isNext ? 'next' : ''}`} key={p.overall}>
                  <span className="code">{code}</span>
                  <span className="mdnote">
                    {isNext
                      ? (away === 0 ? 'your pick — on the clock now'
                        : away != null && away > 0 ? `your next — ${away} pick${away === 1 ? '' : 's'} away`
                          : 'your next')
                      : ''}
                    {from}
                  </span>
                  {isNext && <span className="mdkind next">Next</span>}
                </li>
              );
            }
            const { last, first } = splitName(p.player);
            const club = nflInfoOf(p.player)?.team ?? p.team;
            const wasMoved = moved?.has(p.overall) ?? false;
            return (
              <li className="mdrow" key={p.overall} title={wasMoved ? moved!.get(p.overall) : undefined}>
                <span className="code">{code}</span>
                <span className="mdwho">
                  <b>{shortSurname(last)}</b>
                  {first && <i>{first}</i>}
                  {(p.pos || club) && (
                    <span className="mdmeta">{[p.pos, club].filter(Boolean).join(' · ')}</span>
                  )}
                </span>
                <span className={`mdkind ${p.keeper ? 'keeper' : 'pick'}`}>
                  {p.keeper ? (p.farm ? 'Farm' : 'Keeper') : 'Pick'}{wasMoved ? ' · moved' : ''}
                </span>
              </li>
            );
          })}
        </ul>
        <button type="button" className="btn small wide" onClick={onGoBoard}>
          See the whole board
        </button>
      </section>

      <section className="card mdneeds">
        <div className="striptop">
          <h3 className="striphead">Needs</h3>
          <span className={`mdneedline ${open.length ? 'short' : 'ok'}`}>{needsLine}</span>
        </div>
        <ul className="mdslots">
          {SLOTS.map((pos) => {
            const starters = STARTERS.find((s) => s.pos === pos)?.n ?? 1;
            const n = have(pos);
            const state = slotState(n, starters);
            return (
              <li key={pos} className={state === 'full' ? 'ok' : state === 'empty' ? 'bad' : ''}>
                <span className={`poschip ident pos-${pos}`}>{pos}</span>
                <span>{state}</span>
              </li>
            );
          })}
        </ul>
        <p className="note">
          Counts every man on the roster, keepers included — the starting lineup is what the
          numbers are measured against.
        </p>
      </section>
    </div>
  );
}
